import { motion, useScroll, useTransform } from "framer-motion";
import { FolderPlus, Shield, Brain, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

const HowItWorks = () => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ["start end", "end start"] });
  const lineProgress = useTransform(scrollYProgress, [0.15, 0.6], [0, 1]);

  const steps = [
    {
      number: "01", icon: FolderPlus,
      title: t("howItWorks.step1Title"), subtitle: t("howItWorks.step1Subtitle"), description: t("howItWorks.step1Desc"),
      gradient: "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--accent)) 100%)",
      dot: "hsl(var(--primary))",
      comingSoon: false,
      features: [t("howItWorks.step1Feature1"), t("howItWorks.step1Feature2"), t("howItWorks.step1Feature3")],
    },
    {
      number: "02", icon: Shield,
      title: t("howItWorks.step2Title"), subtitle: t("howItWorks.step2Subtitle"), description: t("howItWorks.step2Desc"),
      gradient: "linear-gradient(135deg, hsl(var(--accent)) 0%, hsl(var(--brand-amber)) 100%)",
      dot: "hsl(var(--brand-amber))",
      comingSoon: false,
      features: [t("howItWorks.step2Feature1"), t("howItWorks.step2Feature2"), t("howItWorks.step2Feature3")],
    },
    {
      number: "03", icon: Brain,
      title: t("howItWorks.step3Title"), subtitle: t("howItWorks.step3Subtitle"), description: t("howItWorks.step3Desc"),
      gradient: "linear-gradient(135deg, hsl(var(--brand-amber)) 0%, hsl(var(--brand-clay)) 100%)",
      dot: "hsl(var(--brand-clay))",
      comingSoon: false,
      features: [t("howItWorks.step3Feature1"), t("howItWorks.step3Feature2"), t("howItWorks.step3Feature3")],
    },
  ];

  return (
    <section id="how-it-works" className="py-24 lg:py-32 bg-background relative overflow-hidden" ref={containerRef}>
      <div className="absolute inset-0 bg-gradient-to-b from-surface via-transparent to-surface pointer-events-none" />
      <motion.div className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-[100px]" animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }} transition={{ duration: 8, repeat: Infinity }} />
      <motion.div className="absolute bottom-20 right-10 w-72 h-72 bg-primary/5 rounded-full blur-[100px]" animate={{ scale: [1.2, 1, 1.2], opacity: [0.5, 0.3, 0.5] }} transition={{ duration: 10, repeat: Infinity }} />

      <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative">
        <motion.div className="text-center max-w-3xl mx-auto mb-16 lg:mb-24" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <motion.span className="inline-block px-4 py-2 rounded-full bg-accent/10 text-primary font-medium text-sm mb-6 border border-accent/20" initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.4 }}>
            {t("howItWorks.badge")}
          </motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold mb-6 tracking-tight">
            {t("howItWorks.headline")}{" "}
            <span className="text-gradient-stripe">{t("howItWorks.headlineHighlight")}</span>
          </h2>
          <p className="text-lg lg:text-xl text-muted-foreground">{t("howItWorks.subheadline")}</p>
        </motion.div>

        {/* Desktop Timeline */}
        <div className="hidden lg:block relative mb-16">
          <div className="absolute top-32 left-[15%] right-[15%] h-1 bg-border rounded-full">
            <motion.div className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary via-primary/80 to-primary/60 rounded-full" style={{ scaleX: lineProgress, transformOrigin: "left" }} />
          </div>
          <div className="grid grid-cols-3 gap-8 relative">
            {steps.map((step, index) => (
              <motion.div key={step.number} className="relative" initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6, delay: index * 0.2 }}>
                <div className="flex justify-center mb-8">
                  <motion.div className="w-24 h-24 rounded-full flex items-center justify-center shadow-lg shadow-black/10 relative z-10" style={{ background: step.gradient }} whileHover={{ scale: 1.1 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}>
                    <step.icon className="w-10 h-10 text-white" />
                    <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-foreground text-background text-sm font-bold flex items-center justify-center shadow-lg">{index + 1}</span>
                    {step.comingSoon && (
                      <Badge variant="secondary" className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-xs whitespace-nowrap shadow-sm">{t("howItWorks.comingSoon")}</Badge>
                    )}
                  </motion.div>
                </div>
                <motion.div className="bg-card rounded-3xl p-8 border border-border/50 hover:border-primary/20 transition-all duration-500 hover:shadow-premium-hover h-full" whileHover={{ y: -5 }}>
                  <h3 className="text-2xl font-bold mb-2 text-foreground">{step.title}</h3>
                  <p className="text-sm font-medium mb-4" style={{ color: step.dot }}>{step.subtitle}</p>
                  <p className="text-muted-foreground leading-relaxed mb-6">{step.description}</p>
                  <ul className="space-y-2">
                    {step.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: step.dot }} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="lg:hidden space-y-8">
          {steps.map((step, index) => (
            <motion.div key={step.number} className="relative" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.1 }}>
              <div className="bg-card rounded-3xl p-6 border border-border/50">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0" style={{ background: step.gradient }}>
                    <step.icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-muted-foreground">{t("howItWorks.step")} {index + 1}</span>
                      {step.comingSoon && <Badge variant="secondary" className="text-xs">{t("howItWorks.comingSoon")}</Badge>}
                    </div>
                    <h3 className="text-xl font-bold text-foreground">{step.title}</h3>
                  </div>
                </div>
                <p className="text-muted-foreground mb-4">{step.description}</p>
                <div className="flex flex-wrap gap-2">
                  {step.features.map((feature) => (
                    <span key={feature} className="px-3 py-1 rounded-full bg-surface text-xs font-medium text-muted-foreground">{feature}</span>
                  ))}
                </div>
              </div>
              {index < steps.length - 1 && (
                <div className="flex justify-center py-4">
                  <ArrowRight className="w-6 h-6 text-muted-foreground rotate-90" aria-hidden="true" />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
